import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  Badge,
  IconButton,
  Grid,
  Card,
  CardContent,
  CardActions,
  CardHeader,
  Fade,
  Divider,
  Avatar,
  Stack,
  TextField,
  Chip,
  InputAdornment
} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import SearchIcon from '@mui/icons-material/Search';
import { useQuery } from 'react-query';
import { useCreatorAuth } from '../../../Hooks/creator-use-auth';
import API from '../../../API';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CreatorCampaignPitchedDialog from './creatorcomponents/creatorCampaignPitchedDialog';
import { getOfferFromCampaign, getHeadersFromKeys } from '../../../Utils/constants';

const statusColorMap = {
  Active: 'success',
  active: 'success',
  Launched: 'success',
  Pending: 'warning',
  Draft: 'warning',
  Completed: 'info',
  Archived: 'default',
  DELETED: 'error',
  deleted: 'error',
  Offered: 'primary',
  Current: 'success',
  Counter: 'warning'
};

const CheckIsPitched = (campaign, username) => {
  const creator = campaign.creators.find(c => c.id === username);
  return (
    (creator?.status === undefined || 
     creator?.status === 'Pitched' || 
     creator?.status === 'Counter') && 
    campaign.campaign_status === 'Launched'
  );
};

const CreatorCollaborations = () => {
  const { creatorToken } = useCreatorAuth();
  const username = creatorToken?.creator_user?.username;
  const token = creatorToken?.token;

  const [collaborations, setCollaborations] = useState([]);
  const [currentFilter, setCurrentFilter] = useState('All');
  const [currentCampaign, setCurrentCampaign] = useState(-1);
  const [openPitch, setOpenPitch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'All';

  const navigate = useNavigate();

  const fetchCampaigns = async () => {
    if (!token) return [];
    const response = await API.creatorConnect.campaigns(token);
    return Array.isArray(response) ? response : response?.data || [];
  };

  const fetchPartnerships = async () => {
    const response = await API.creatorConnect.getPartnerships();
    return Array.isArray(response) ? response : response?.data || [];
  };

  const fetchCastingCalls = async () => {
    const response = await API.castingCalls.creatorFetch();
    return Array.isArray(response) ? response : response?.casting_calls || [];
  };

  const fetchDeals = async () => {
    if (!token) return [];
    try {
      const response = await API.dealsIO.creatorFetch();
      return Array.isArray(response) ? response : [response];
    } catch (error) {
      console.error("Error fetching deals:", error);
      return [];
    }
  };

  const {
    isLoading: campaignsLoading,
    data: campaigns = [],
    refetch: refetchCampaigns,
  } = useQuery(['campaigns', 'creator', username], fetchCampaigns);

  const {
    isLoading: partnershipsLoading,
    data: partnerships = [],
    refetch: refetchPartnerships,
  } = useQuery(['creator', 'partnerships'], fetchPartnerships);

  const {
    isLoading: castingCallsLoading,
    data: castingCalls = [],
    refetch: refetchCastingCalls,
  } = useQuery(['castingCalls', 'creator'], fetchCastingCalls);

  const {
    isLoading: dealsLoading,
    data: deals = [],
    refetch: refetchDeals,
  } = useQuery(['deals', 'creator'], fetchDeals);

  useEffect(() => {
    const campaignsWithType = campaigns.map(campaign => ({
      ...campaign,
      type: 'campaign',
    }));

    const partnershipsWithType = partnerships.map(partnership => ({
      ...partnership,
      type: 'partnership',
    }));

    const castingCallsWithType = castingCalls.map(castingCall => ({
      ...castingCall,
      type: 'casting_call',
      isApplied: castingCall.status === 'applied',
    }));

    const dealsWithType = deals.map(deal => ({
      ...deal,
      type: 'deal',
    }));

    const combinedCollaborations = [
      ...campaignsWithType,
      ...partnershipsWithType,
      ...castingCallsWithType,
      ...dealsWithType,
    ];

    setCollaborations(combinedCollaborations);
  }, [campaigns, partnerships, castingCalls, deals]);

  useEffect(() => {
    if (!openPitch) refetchCampaigns();
  }, [openPitch, refetchCampaigns]);

  const loading = campaignsLoading || partnershipsLoading || castingCallsLoading || dealsLoading;

  const getDealStatus = (deal) => {
    if (deal.amount_due === 0) return 'Completed';
    if (deal.amount_paid === 0 && deal.amount_due > 0) return 'Offered';
    if (deal.amount_paid > 0 && deal.amount_due > 0) return 'Current';
    return 'All';
  };

  useEffect(() => {
    const currentTab = searchParams.get('tab') || 'All';
    if (currentTab !== currentFilter) setCurrentFilter(currentTab);
  }, [searchParams]);

  const handleTabChange = (_, newValue) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', newValue);
    setSearchParams(newParams);
    setCurrentFilter(newValue);
  };

  const filteredCollaborations = useMemo(() => {
    return collaborations.filter(item => {
      // Filter by tab selection
      let tabMatch = true;
      if (item.type === 'campaign') {
        const creator = item.creators.find(c => c.id === username);
        switch (currentFilter) {
          case 'All':
            tabMatch = !['Archived', 'Draft', 'DELETED'].includes(item.campaign_status);
            break;
          case 'Current':
            tabMatch = creator?.status && 
                      !['Declined', 'Dropped', 'Pitched', 'Counter'].includes(creator.status) && 
                      item.campaign_status === 'Launched';
            break;
          case 'Offered':
            tabMatch = (creator?.status === undefined || 
                      ['Pitched', 'Counter'].includes(creator?.status)) && 
                      item.campaign_status === 'Launched';
            break;
          case 'Completed':
            tabMatch = creator?.status && 
                      !['Declined', 'Dropped', 'Pitched', 'Counter'].includes(creator.status) && 
                      item.campaign_status === 'Completed';
            break;
          default:
            tabMatch = true;
        }
      } else if (item.type === 'partnership') {
        switch (currentFilter) {
          case 'All': 
            tabMatch = !['Archived', 'Draft', 'DELETED'].includes(item.status);
            break;
          case 'Current': 
            tabMatch = item.status === 'Active';
            break;
          case 'Offered': 
            tabMatch = ['pending', 'Draft'].includes(item.status);
            break;
          case 'Completed': 
            tabMatch = item.status === 'Completed';
            break;
          default: 
            tabMatch = true;
        }
      } else if (item.type === 'casting_call') {
        switch (currentFilter) {
          case 'All': 
            tabMatch = item.status !== 'closed';
            break;
          case 'Current': 
            tabMatch = ['active', 'applied'].includes(item.status);
            break;
          case 'Offered': 
            tabMatch = item.status === 'open';
            break;
          case 'Completed': 
            tabMatch = item.status === 'completed';
            break;
          default: 
            tabMatch = true;
        }
      } else if (item.type === 'deal') {
        const dealStatus = getDealStatus(item);
        tabMatch = currentFilter === 'All' || dealStatus === currentFilter;
      }

      // Filter by search query
      const searchMatch = 
        item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.id?.toString().includes(searchQuery);

      return tabMatch && searchMatch;
    });
  }, [collaborations, currentFilter, username, searchQuery]);

  const getPriority = (item) => {
    if (item.type === 'partnership') {
      if (item.status === 'Active') return 0;
      if (['pending', 'Draft'].includes(item.status)) return 1;
      if (item.status === 'Completed') return 2;
      return 3;
    }

    if (item.type === 'deal') {
      const ds = getDealStatus(item);
      if (ds === 'Current') return 0;
      if (ds === 'Offered') return 1;
      if (ds === 'Completed') return 2;
      return 3;
    }

    if (item.type === 'casting_call') {
      if (item.isApplied || item.status === 'active') return 0;
      if (item.status === 'open') return 1;
      if (item.status === 'completed') return 2;
      return 3;
    }

    if (item.type === 'campaign') {
      const creator = item.creators.find(c => c.id === username);
      const accepted = creator?.status && 
                      !['Declined', 'Dropped', 'Pitched', 'Counter'].includes(creator.status);

      if (accepted && item.campaign_status === 'Launched') return 0;
      if ((!creator?.status || ['Pitched', 'Counter'].includes(creator?.status)) && 
          item.campaign_status === 'Launched') return 1;
      if (accepted && item.campaign_status === 'Completed') return 2;
      return 3;
    }

    return 3;
  };

  const sortedCollaborations = useMemo(() => {
    return [...filteredCollaborations].sort((a, b) => {
      const priorityDiff = getPriority(a) - getPriority(b);
      if (priorityDiff !== 0) return priorityDiff;
      
      const dateA = new Date(a.updated_at || a.created_at || 0);
      const dateB = new Date(b.updated_at || b.created_at || 0);
      return dateB - dateA;
    });
  }, [filteredCollaborations]);

  const offeredCount = useMemo(() => {
    return collaborations.reduce((count, item) => {
      if (item.type === 'campaign') {
        const creator = item.creators.find(c => c.id === username);
        if ((!creator?.status || ['Pitched', 'Counter'].includes(creator?.status)) && 
            item.campaign_status === 'Launched') {
          count++;
        }
      } else if (item.type === 'partnership') {
        if (['pending', 'Draft'].includes(item.status)) count++;
      } else if (item.type === 'casting_call') {
        if (item.status === 'open') count++;
      } else if (item.type === 'deal') {
        if (getDealStatus(item) === 'Offered') count++;
      }
      return count;
    }, 0);
  }, [collaborations, username]);

  const handleItemClick = (item) => {
    if (item.type === 'campaign') {
      if (CheckIsPitched(item, username)) {
        setCurrentCampaign(item.id);
        setOpenPitch(true);
      } else {
        navigate(`/creatorconnect/campaigns/${item.id}`);
      }
    } else if (item.type === 'partnership') {
      navigate(`/creatorconnect/partnerships/${item.id}`);
    } else if (item.type === 'casting_call') {
      navigate(`/creatorconnect/casting-calls/${item.id}${item.isApplied ? '/applied' : ''}`);
    } else if (item.type === 'deal') {
      navigate(`/creatorconnect/deals/${item.id}`);
    }
  };

  const handleClosePitch = () => setOpenPitch(false);

  const getButtonLabel = item => {
    if (item.type === 'campaign') return currentFilter !== 'Offered' ? 'Manage Campaign' : 'See Deal';
    if (item.type === 'partnership') return 'Manage Partnership';
    if (item.type === 'casting_call') return item.isApplied ? 'View Application' : 'View Details';
    if (item.type === 'deal') return 'Manage Deal';
    return 'Action';
  };

  const handleShare = (item) => {
    alert(`Sharing ${item.type} - ${item.name || item.id}`);
  };

  // Dashboard metrics
  const totalMoneyAvailable = useMemo(() => {
    return deals.reduce((sum, deal) => sum + (deal.amount_due || 0), 0);
  }, [deals]);

  const aggregateCollabs = useMemo(() => {
    const activeCampaigns = campaigns.filter(c => 
      c.campaign_status === 'Launched' && 
      c.creators.some(creator => 
        creator.id === username && 
        !['Declined', 'Dropped', 'Pitched', 'Counter'].includes(creator.status)
      )
    ).length;

    const activePartnerships = partnerships.filter(p => 
      p.status === 'Active'
    ).length;

    return activeCampaigns + activePartnerships;
  }, [campaigns, partnerships, username]);

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <CircularProgress />
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, backgroundColor: '#f9f9f9', minHeight: '100vh' }}>
      {/* Simplified Dashboard */}
      <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
        <Typography variant="h4" gutterBottom>Collaborations Dashboard</Typography>
        
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>Total Money Available</Typography>
              <Typography variant="h3" color="primary">
                ${totalMoneyAvailable.toLocaleString()}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>Total Active Collabs</Typography>
              <Typography variant="h3" color="secondary">
                {aggregateCollabs}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* Search and Filter Section */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search collaborations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            backgroundColor: 'white',
            borderRadius: 2,
            maxWidth: { md: '400px' },
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            }
          }}
        />
        
        <Paper elevation={2} square sx={{ flexGrow: 1, borderRadius: 3 }}>
          <Tabs
            value={currentFilter}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label="All" value="All" />
            <Tab label="Current" value="Current" />
            <Tab
              label={
                <Badge
                  badgeContent={offeredCount}
                  color="secondary"
                  overlap="rectangular"
                  sx={{ padding: 1 }}
                >
                  Offered
                </Badge>
              }
              value="Offered"
            />
            <Tab label="Completed" value="Completed" />
          </Tabs>
        </Paper>
      </Box>

      <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
        {currentFilter === 'All' ? 'All Collaborations' : `${currentFilter} Collaborations`}
      </Typography>

      {/* Collaboration Cards Grid */}
      <Grid container spacing={3}>
        {sortedCollaborations.length > 0 ? (
          sortedCollaborations.map((item, index) => {
            const isCounter = item.type === 'campaign' && 
              item.creators?.find(c => c.id === username)?.status === 'Counter';

            return (
              <Fade in key={`${item.type}-${item.id}`} style={{ transitionDelay: `${index * 100}ms` }}>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      border: isCounter ? '2px solid #ffeb3b' : 'none',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 3
                      }
                    }}
                  >
                    <CardHeader
                      avatar={
                        <Avatar 
                          src={item.company_pfphref || '/placeholder.png'}
                          sx={{ bgcolor: 'primary.main' }}
                        >
                          {item.type.charAt(0).toUpperCase()}
                        </Avatar>
                      }
                      title={item.name || `#${item.id}`}
                      titleTypographyProps={{ 
                        sx: { 
                          fontWeight: 'bold',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        } 
                      }}
                      subheader={
                        <Chip 
                          label={item.type === 'campaign' ? 'Campaign' : 
                                 item.type === 'partnership' ? 'Partnership' : 
                                 item.type === 'casting_call' ? 'Casting Call' : 'Deal'}
                          size="small"
                          color={item.type === 'campaign' ? 'secondary' : 'primary'}
                        />
                      }
                    />
                    
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Stack spacing={1.5}>
                        {/* Status Chip */}
                        <Box>
                          {item.type === 'campaign' ? (
                            <Chip 
                              label={item.campaign_status} 
                              color={statusColorMap[item.campaign_status] || 'default'} 
                              size="small" 
                            />
                          ) : item.type === 'partnership' ? (
                            <Chip 
                              label={item.status} 
                              color={statusColorMap[item.status] || 'default'} 
                              size="small" 
                            />
                          ) : item.type === 'casting_call' ? (
                            <Chip 
                              label={item.status.charAt(0).toUpperCase() + item.status.slice(1)} 
                              color={statusColorMap[item.status] || 'default'} 
                              size="small" 
                            />
                          ) : (
                            <Chip 
                              label={getDealStatus(item)} 
                              color={statusColorMap[getDealStatus(item)] || 'default'} 
                              size="small" 
                            />
                          )}
                        </Box>
                        
                        {/* Item-specific details */}
                        {item.type === 'campaign' && (
                          <>
                            <Typography variant="body2">
                              <strong>Offer:</strong> {getOfferFromCampaign(item, username)}
                            </Typography>
                            {isCounter && (
                              <Typography variant="body2" sx={{ color: 'warning.main', fontStyle: 'italic' }}>
                                Deal Pending Client Approval
                              </Typography>
                            )}
                          </>
                        )}
                        
                        {item.type === 'partnership' && (
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {item.description || 'No description available'}
                          </Typography>
                        )}
                        
                        {item.type === 'casting_call' && (
                          <>
                            <Typography variant="body2">
                              <strong>Budget:</strong> ${item.budget_min} - ${item.budget_max}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Platforms:</strong> {item.platforms?.join(', ') || 'N/A'}
                            </Typography>
                          </>
                        )}
                        
                        {item.type === 'deal' && (
                          <>
                            <Typography variant="body2">
                              <strong>Amount Due:</strong> ${item.amount_due}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Amount Paid:</strong> ${item.amount_paid}
                            </Typography>
                          </>
                        )}
                      </Stack>
                    </CardContent>
                    
                    <CardActions sx={{ justifyContent: 'space-between', pt: 0 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleItemClick(item)}
                        sx={{ borderRadius: 2 }}
                      >
                        {getButtonLabel(item)}
                      </Button>
                      <IconButton onClick={() => handleShare(item)}>
                        <ShareIcon />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              </Fade>
            );
          })
        ) : (
          <Grid item xs={12}>
            <Typography align="center" variant="subtitle1" color="textSecondary">
              {searchQuery ? 
                'No matching collaborations found' : 
                `No ${currentFilter === 'All' ? '' : currentFilter + ' '}collaborations available`
              }
            </Typography>
          </Grid>
        )}
      </Grid>

      <CreatorCampaignPitchedDialog
        openDialog={openPitch}
        handleCloseDialog={handleClosePitch}
        campaignId={currentCampaign}
      />
    </Box>
  );
};

export default CreatorCollaborations;